/**
 * PDF页面缓存管理Store
 *
 * 该Store用于管理PDF文档页面的缩略图缓存，主要功能包括：
 * 1. 缓存PDF页面的缩略图（base64格式）
 * 2. 管理缓存的生命周期（10分钟过期）
 * 3. 提供缓存的增删改查操作
 *
 * 缓存策略：
 * - 只缓存最近一个PDF文档
 * - 缓存过期时间为10分钟
 * - 每次访问缓存会更新最后访问时间
 * - 过期缓存会被自动清理
 */

import { defineStore } from 'pinia'

interface ThumbnailCache {
  totalPages: number
  thumbnails: Map<number, string> // 页码 -> 缩略图base64数据
  lastAccessed: number
  pdfUrl: string
}

interface PdfPageCacheState {
  cache: ThumbnailCache | null
}

/**
 * 缓存配置常量
 */
// 缓存过期时间：10分钟
const CACHE_EXPIRE_TIME = 10 * 60 * 1000

// 创建一个定时器来清理过期缓存
let expirationTimer: number | null = null

export const usePdfPageCacheStore = defineStore('pdfPageCache', {
  state: (): PdfPageCacheState => ({
    cache: null,
  }),

  actions: {
    /**
     * 检查当前缓存是否已过期
     * @returns {boolean} true表示缓存已过期，false表示缓存仍然有效
     */
    isCacheExpired(): boolean {
      return this.cache
        ? Date.now() - this.cache.lastAccessed > CACHE_EXPIRE_TIME
        : true
    },

    /**
     * 清理过期的缓存数据
     * 当缓存超过设定的过期时间时，清除缓存并停止定时器
     */
    clearExpiredCache() {
      if (this.cache && this.isCacheExpired()) {
        this.cache = null
        // 清理定时器
        if (expirationTimer !== null) {
          clearTimeout(expirationTimer)
          expirationTimer = null
        }
      }
    },

    /**
     * 获取指定PDF文档指定页码的缩略图
     * @param pdfUrl - PDF文档的URL
     * @param pageNum - 需要获取缩略图的页码
     * @returns {string | null} 返回缩略图的base64数据，如果缓存不存在则返回null
     */
    getThumbnail(pdfUrl: string, pageNum: number): string | null {
      if (!this.cache || this.cache.pdfUrl !== pdfUrl) return null

      // 更新最后访问时间
      this.cache.lastAccessed = Date.now()
      return this.cache.thumbnails.get(pageNum) || null
    },

    /**
     * 获取指定PDF文档的总页数
     * @param pdfUrl - PDF文档的URL
     * @returns {number | null} 返回PDF的总页数，如果缓存不存在则返回null
     */
    getTotalPages(pdfUrl: string): number | null {
      if (!this.cache || this.cache.pdfUrl !== pdfUrl) return null

      this.cache.lastAccessed = Date.now()
      return this.cache.totalPages
    },

    /**
     * 添加或更新PDF页面的缩略图缓存
     * @param pdfUrl - PDF文档的URL，用于标识不同的PDF文件
     * @param pageNum - PDF页码
     * @param totalPages - PDF总页数
     * @param thumbnail - 缩略图的base64数据
     */
    addThumbnail(
      pdfUrl: string,
      pageNum: number,
      totalPages: number,
      thumbnail: string
    ) {
      // 如果是新的PDF或没有缓存，创建新的缓存
      if (!this.cache || this.cache.pdfUrl !== pdfUrl) {
        // 清理旧的定时器
        if (expirationTimer !== null) {
          clearTimeout(expirationTimer)
        }

        this.cache = {
          totalPages,
          thumbnails: new Map(),
          lastAccessed: Date.now(),
          pdfUrl,
        }

        // 启动新的定时器
        expirationTimer = setTimeout(() => {
          this.clearExpiredCache()
        }, CACHE_EXPIRE_TIME) as unknown as number
      }

      // 更新缩略图
      this.cache.thumbnails.set(pageNum, thumbnail)
      this.cache.lastAccessed = Date.now()
    },

    /**
     * 清除指定PDF文档的缓存
     * 如果不指定pdfUrl，则清除当前缓存的文档
     * @param pdfUrl - [可选] PDF文档的URL
     */
    clearCache(pdfUrl?: string) {
      if (!pdfUrl || (this.cache && this.cache.pdfUrl === pdfUrl)) {
        this.cache = null
        // 清理定时器
        if (expirationTimer !== null) {
          clearTimeout(expirationTimer)
          expirationTimer = null
        }
      }
    },

    /**
     * 清除所有缓存数据
     * 同时会停止定时器的运行
     */
    clearAllCache() {
      this.cache = null
      // 清理定时器
      if (expirationTimer !== null) {
        clearTimeout(expirationTimer)
        expirationTimer = null
      }
    },
  },
})
